import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';

import { SegmentActions } from '../../app-global/analytics/segment/segment.actions';

import { TeamListStateActions } from './team-list-state.actions';

/** User-facing team-settings fields tracked by team_updated. The updateOne changeset can also carry
 *  archived / deleted / removedUsers (archive / delete / reactivate / member-removal flows) — those
 *  have their own events or aren't settings edits, so they're excluded to avoid a double-fire. */
const TEAM_SETTINGS_FIELDS = ['name', 'private', 'project'] as const;

/**
 * Root-scoped because it hooks the global `TeamListStateActions.updateOneSuccess`. The rename source
 * (`team-card`) renders via the global `detail-view` route, not just Settings — a feature-scoped effect
 * would silently miss renames done outside Settings. Team events whose action is settings-sourced
 * (created / archived / deleted / set-as-leadership) stay in the settings `TeamsAnalyticsEffects`.
 */
@Injectable()
export class TeamListAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  /** Fires once per settled team-settings edit (name / private / project). Other `updateOne` change
   *  sets — archive / delete / reactivate / member-removal — are filtered out (dedicated events). */
  trackTeamUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.updateOneSuccess),
      map(({ id, update }) => ({
        id,
        changedFields: TEAM_SETTINGS_FIELDS.filter(field => field in (update ?? {})),
      })),
      filter(({ changedFields }) => changedFields.length > 0),
      map(({ id, changedFields }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TEAM_UPDATED,
          params: { entityId: id, changedFields: changedFields.join(',') },
        })
      )
    )
  );
}
